import React, { useContext } from "react";
import fp, { isEmpty } from "lodash/fp";
import { Col, Row } from "react-bootstrap";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { Field, formValueSelector } from "redux-form";

import { CheckButton, RadioButton } from "components";
import { staticDropdownValuesService } from "services";
import { AmexOptBlueOrEsaTypes } from "types";
import { required } from "validators/validation";

import FeeBlockContext from "../feeBlockContext";
import FeesTemplateContext from "../feesTemplateContext";
import TemplateField from "../templateField";

import styles from "./feesMonthly.module.scss";

const FeesMonthlyFields = ({
	formValues,
	businessFormName = "businessForm",
	isMerchantFeesPage,
}) => {
	const selectorBusinessForm =
		businessFormName && formValueSelector(businessFormName);

	const monthlyAMEXFormValue = useSelector(
		fp.flow(
			state =>
				businessFormName && selectorBusinessForm(state, "monthlyAMEX"),
		),
	)?.replace(/,/g, "");
	const avgTicketAMEXFormValue = useSelector(
		fp.flow(
			state =>
				businessFormName &&
				selectorBusinessForm(state, "avgTicketAMEX"),
		),
	)?.replace(/,/g, "");
	const highTicketAMEXFormValue = useSelector(
		fp.flow(
			state =>
				businessFormName &&
				selectorBusinessForm(state, "highTicketAMEX"),
		),
	)?.replace(/,/g, "");

	const {
		validators,
		isMerchant,
		isFiservFspProcessor,
		application: { isPaperApplication },
	} = useContext(FeesTemplateContext);

	const { data: feeMonths } = useQuery(
		"feeMonths",
		staticDropdownValuesService.getFeeMonths,
	);

	const { data: pciNonComplianceFees } = useQuery(
		"pciNonComplianceFees",
		staticDropdownValuesService.getPciNonComplianceFees,
	);

	const showInactiveFee =
		formValues?.inactiveFee && Number(formValues?.inactiveFee) !== 0;

	const toBeBilledMonthDisabled = formValues?.toBeBilled !== "1";

	const isAmexOptBlueDisabled =
		!formValues?.isActiveAmexOptBlueOrEsaSection &&
		+monthlyAMEXFormValue +
			+highTicketAMEXFormValue +
			+avgTicketAMEXFormValue ===
			0 &&
		!isEmpty(businessFormName);

	const isDisabledAmexOptBlue = +monthlyAMEXFormValue >= 1000000;

	return (
		<FeeBlockContext.Provider value="MonthlyFees">
			<h4 className={styles.header}>Monthly Fees</h4>
			<Row>
				<Col md={5}>
					<TemplateField
						name="customerServiceFee"
						label="Customer Service Fee, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.customerServiceFee}
						hideRequiredMark={isPaperApplication}
					/>
					<TemplateField
						name="regulatoryProductFee"
						label="Regulatory Product Fee, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.regulatoryProductFee}
						hideRequiredMark={isPaperApplication}
					/>
					<TemplateField
						name="wirelessFee"
						label="Wireless Fee, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.wirelessFee}
						hideRequiredMark={isPaperApplication}
					/>
					<TemplateField
						name="equipmentFee"
						label="Equipment Fee, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.equipmentFee}
						hideRequiredMark={isPaperApplication}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<TemplateField
						name="onlineReportingFee"
						label="Online Reporting, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.onlineReportingFee}
						hideRequiredMark={isPaperApplication}
					/>
					<TemplateField
						name="softwareFee"
						label="Software Fee, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.softwareFee}
						hideRequiredMark={isPaperApplication}
					/>
					<TemplateField
						name="statementFee"
						label="Statement Fee, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.statementFee}
						hideRequiredMark={isPaperApplication}
					/>
					{isMerchantFeesPage && (
						<TemplateField
							name="riseFreeProgramFee"
							label="Rise Fee Program Fee, $"
							placeholder="0.000000"
							placeholderClassName={
								isPaperApplication
									? "placeholder-value"
									: "placeholder-primary"
							}
							{...validators.riseFreeProgramFee}
							hideRequiredMark={isPaperApplication}
						/>
					)}
				</Col>
			</Row>
			<hr className="line" />
			<p className={styles.header}>PCI Compliance Fee</p>
			<Row>
				<Col md={5}>
					<TemplateField
						name="pciComplianceFee"
						label="PCI Compliance Fee, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.pciComplianceFee}
						hideRequiredMark={isPaperApplication}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<TemplateField
						type={
							!isMerchant && isFiservFspProcessor
								? "dropdown"
								: "input"
						}
						items={pciNonComplianceFees}
						name="pciNonCompilance"
						label="PCI Non-Compliance Fee, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						validate={
							!isMerchant &&
							isFiservFspProcessor &&
							!isPaperApplication && [required]
						}
						{...validators.pciNonComplianceFee}
						hideRequiredMark={isPaperApplication}
					/>
				</Col>
			</Row>
			<div className="mb-4">To Be Billed</div>
			<div className="d-flex flex-row mb-3">
				<Field
					component={RadioButton}
					type="radio"
					name="toBeBilled"
					label="Monthly"
					value="0"
					className="mr-4"
					id="toBeBilledMonthly"
				/>
				<Field
					component={RadioButton}
					type="radio"
					name="toBeBilled"
					label="Annually"
					value="1"
					id="toBeBilledAnnually"
				/>
			</div>
			<Row>
				<Col md={5}>
					<TemplateField
						name="monthPCIAnnualFeeCharged"
						label="Month PCI Annual Fee Charged"
						type="dropdown"
						items={feeMonths}
						disabled={toBeBilledMonthDisabled}
						useFirstAsDefault
						hideRequiredMark={isPaperApplication}
					/>
				</Col>
			</Row>
			<hr className="line" />
			<Row>
				<Col md={5}>
					<TemplateField
						name="monthlyMinimumFee"
						label="Monthly Minimum Fee, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.monthlyMinimumFee}
						hideRequiredMark={isPaperApplication}
					/>
					<TemplateField
						name="tinInvalidFee"
						label="TIN Invalid Fee, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						disabled
						isPlainText
						{...validators.tinInvalidFee}
						hideRequiredMark={isPaperApplication}
					/>
					<TemplateField
						name="achRejectFee"
						label="ACH Reject Fee, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						disabled
						isPlainText
						hideRequiredMark={isPaperApplication}
					/>
					<TemplateField
						name="merchantMonitoringFee"
						label="Merchant Monitoring Fee,&nbsp;$"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						disabled
						isPlainText
						hideRequiredMark={isPaperApplication}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					{showInactiveFee && (
						<TemplateField
							name="inactiveFee"
							label="Inactivity Fee, $"
							disabled
							{...validators.inactivityFee}
						/>
					)}
					<TemplateField
						name="chargebackFee"
						label="Chargeback Fee, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.chargebackFee}
						disabled={!isMerchant}
						isPlainText={!isMerchant}
						hideRequiredMark={isPaperApplication}
					/>
				</Col>
			</Row>
			<hr className="line" />
			<Field
				component={CheckButton}
				label="Pin-Debit"
				id="isPinDebitEnabled"
				name="isPinDebitEnabled"
				hideRequiredMark
			/>
			{formValues?.isPinDebitEnabled && (
				<div className="pl-4 mt-2">
					<Field
						component={CheckButton}
						label="Pin-Debit Sponsorship (Pass Through Debit Network Fee)"
						id="sponsorShip"
						name="pinDebitSponsorship"
						hideRequiredMark
					/>
					<Row className="mt-3">
						<Col md={5}>
							<TemplateField
								name="monthlyAccessFee"
								label="Monthly Access Fee, $"
								hideRequiredMark
								placeholder="0.000000"
								placeholderClassName={
									isPaperApplication
										? "placeholder-value"
										: "placeholder-primary"
								}
								{...validators.monthlyAccessFee}
							/>
							<TemplateField
								name="otherVolumeFee"
								label="Other Volume Fee, %"
								hideRequiredMark
								placeholder="0.000000"
								placeholderClassName={
									isPaperApplication
										? "placeholder-value"
										: "placeholder-primary"
								}
								{...validators.otherVolumeFee}
							/>
						</Col>
						<Col md={{ span: 5, offset: 2 }}>
							<TemplateField
								name="authorizationFeeOtherItemFee"
								label="Authorization Fee / Other Item Fee, $"
								hideRequiredMark
								placeholder="0.000000"
								placeholderClassName={
									isPaperApplication
										? "placeholder-value"
										: "placeholder-primary"
								}
								{...validators.authorizationFeeOtherItemFee}
							/>
						</Col>
					</Row>
				</div>
			)}
			<hr className="line" />
			<div className={styles.header}>Voyager &amp; WEX Gateway Fees</div>
			<Row className="pl-4">
				<Col md={5}>
					<TemplateField
						name="voyagerQual"
						label="Voyager Qual Fee, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.voyagerQual}
						hideRequiredMark={isPaperApplication}
					/>
					<TemplateField
						name="voyagerOtherItemRate"
						label="Voyager Other Item Rate, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.voyagerOtherItemRate}
						hideRequiredMark={isPaperApplication}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<TemplateField
						name="wexQual"
						label="WEX Gateway Qual Fee, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.wexQual}
						hideRequiredMark={isPaperApplication}
					/>
					<TemplateField
						name="wexOtherItemRate"
						label="WEX Gateway Other Item Rate, $"
						placeholder="0.000000"
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.wexOtherItemRate}
						hideRequiredMark={isPaperApplication}
					/>
				</Col>
			</Row>
			<hr className="line" />
			<div className={styles.addressTitle}>
				Amex OptBlue or ESA?{" "}
				{isAmexOptBlueDisabled && (
					<span className="text-warning ml-4">
						Amex desired limits should be specified in order to
						enable this section.
					</span>
				)}
			</div>
			<Row>
				<Col md={12}>
					<div className="pt-4 mb-3">
						<div className="d-flex">
							<Field
								component={RadioButton}
								value={AmexOptBlueOrEsaTypes.AmexOptBlue.value}
								type="radio"
								className={styles.radioButton}
								name={AmexOptBlueOrEsaTypes.AmexOptBlue.name}
								label={
									AmexOptBlueOrEsaTypes.AmexOptBlue
										.displayName
								}
								id="amexTypeOptBlue"
								disabled={
									isAmexOptBlueDisabled ||
									isDisabledAmexOptBlue
								}
							/>
							<Field
								component={RadioButton}
								value={AmexOptBlueOrEsaTypes.ESA.value}
								type="radio"
								className={styles.radioButton}
								name={AmexOptBlueOrEsaTypes.ESA.name}
								label={AmexOptBlueOrEsaTypes.ESA.displayName}
								id="amexTypeESA"
								disabled={isAmexOptBlueDisabled}
							/>
						</div>
					</div>
				</Col>
			</Row>
			<Row>
				<Col md={5}>
					<TemplateField
						name="amexESANumber"
						label="Existing ESA Number If Applicable"
						disabled={
							formValues?.amexType !==
								AmexOptBlueOrEsaTypes.ESA.value ||
							isAmexOptBlueDisabled
						}
						{...validators.amexESANumber}
						hideRequiredMark={isPaperApplication}
					/>
				</Col>
			</Row>
			<Row>
				<Col md={5}>
					<TemplateField
						name="amexOtherItemFee"
						label="Other Item Fee, $"
						placeholder="0.000000"
						disabled={
							formValues?.amexType !==
								AmexOptBlueOrEsaTypes.AmexOptBlue.value ||
							isAmexOptBlueDisabled
						}
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.amexOtherItemFee}
						hideRequiredMark={isPaperApplication}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<TemplateField
						name="amexOtherVolumeFee"
						label="Other Volume Fee, %"
						placeholder="0.000000"
						disabled={
							formValues?.amexType !==
								AmexOptBlueOrEsaTypes.AmexOptBlue.value ||
							isAmexOptBlueDisabled
						}
						placeholderClassName={
							isPaperApplication
								? "placeholder-value"
								: "placeholder-primary"
						}
						{...validators.amexOtherVolumeFee}
						hideRequiredMark={isPaperApplication}
					/>
				</Col>
			</Row>
		</FeeBlockContext.Provider>
	);
};

export default FeesMonthlyFields;
